/**
 * Diretiva que cria um botão com dropdown da lista de ações pertinentes aos
 * recursos, ou desabilita um botão, ou link para uma única ação
 * 
 * @author Lucas.Rodrigues
 * 
 * @since 1.0.0
 */
namespace app.support.command {
    'use strict';
    
    import ITask = app.tarefas.minhasTarefas.ITask;
    import IPermission = app.autenticacao.IPermission;
    import AuthService = app.autenticacao.AuthService;
    
    export interface CommandListDirectiveScope extends ng.IScope {
        commandsConfig: CommandConfig[];
        targets: CommandTarget[];
        targetType: string;
        context: string;
    
        go(commandConfig: CommandConfig): void;
    }
    
    /**
     * Diretiva de lista de comandos
     * Ex. de uso: 
     * &lt;commands targets="vm.recursos" context="autuacao" target-type="Processo"&gt;&lt;/commands&gt;
     */
    export class CommandListDirective implements ng.IDirective {
        
        public restrict: string = 'AE';
        public templateUrl: string = 'app/main/support/command/commands.tpl.html';
        public scope: Object = {
                targets : '=', //obrigatório, recursos que sofrerão
                targetType : '@', //opcional, filtra os comandos de um determinado objeto alvo
                context : '@', //opcional, filtra os comandos de um determinado contexto	
        };
    
        public constructor(private commandService: CommandService, private $state: ng.ui.IStateService) { }
    
        public link: ng.IDirectiveLinkFn = ($scope: CommandListDirectiveScope): void => {
            
            let filter: CommandFilter = null;
        
            if ($scope.context || $scope.targetType) {
                filter = <CommandFilter> {
                    context: $scope.context,
                    targetType: $scope.targetType
                };
            }
            //remove undefined property
            filter = _.pickBy(filter);
            
            var listCommands = () => {
                //serviço que lista os comandos
                this.commandService.listMatched($scope.targets, filter)
                    .then((commandsConfig: CommandConfig[]) => {
                        $scope.commandsConfig = commandsConfig;
                    });
            };
            
            //os comandos devem ser recarregadas sempre que a quantidade de objetos mudar
            $scope.$watchCollection(() => $scope.targets, listCommands);
            
            //vai para o estado de uma ação selecionada, passando o target como parâmetro
            $scope.go = (commandConfig: CommandConfig) => { 
                let params: any = {	};
                let targets = $scope.targets;  
                
                if (angular.isArray(targets) && commandConfig.target.mode !== "One") {
                    params.targets = targets;
                } else {
                    params.target = angular.isArray(targets) ? targets[0]: targets;
                }
                this.$state.go(commandConfig.route.stateName, params);
            };
        }
    
        public static factory(): ng.IDirectiveFactory {
            /** @ngInject **/
            let directive = (commandService: CommandService, $state: ng.ui.IStateService) => new CommandListDirective(commandService, $state);
            return directive;
        }
    }
    
    export interface CommandDirectiveScope extends ng.IScope {
        command: Command;
        commandId: string;
        validator: string;
        id: string;
    }
    
    /**
     * Bot&atilde;o de um comando espec&iacute;fico
     * Ex. de uso: 
     * &lt;button id="registrar" commandId="registrar" command="vm.command" validator="validadorRegistro"&gt;Executar&lt;/button&gt; 
     */
    export class CommandDirective implements ng.IDirective {
        
        public restrict: string = 'A';
        public scope: Object = {
            command : '=', //obrigatório, comando que será validado
            commandId : '@', //opcional, id do comando que será validado, caso o id não possa ser usado            
            validator: '@', //opcional, validador para o comando, caso não informado procura por um com mesmo id do comando
            id: '@' //obrigatório, id do comando que será validado
        };
        
        public constructor(private commandService: CommandService, private $stateParams: ng.ui.IStateParamsService,
                private messagesService: app.support.messaging.MessagesService, private $rootScope: ng.IRootScopeService) { }
        
        public link: ng.IDirectiveLinkFn = ($scope: CommandDirectiveScope, element: ng.IAugmentedJQuery): void => {
            
            if (angular.isUndefined($scope.command)) {
                throw new Error("Comando não definido!");
            }
            
            let stopClick = (event: JQueryEventObject) => {
                event.preventDefault();
                event.stopPropagation();
            }
            
            $scope.$parent["$checkTask"] = (expression, $event) => {
                if (this.$stateParams["task"] && !this.$stateParams["task"].selfAssigned) {
                    this.$rootScope.$broadcast("tentouExecutarTarefaSemAssumir");
                    this.messagesService.error("É necessário assumir a tarefa antes de executá-la.");
                    return false;
                } else {
                    return $scope.$parent.$eval(expression, {"$event": $event});
                }
            }
            
            let commandId: string =  angular.isString($scope.commandId) ? $scope.commandId : $scope.id;
            let validatorId: string = angular.isString($scope.validator) ? $scope.validator : commandId;
            
            if (angular.isString(commandId) && commandId.length > 0) {
            
                let task: ITask = <ITask> this.$stateParams["task"];
                
                if (angular.isObject(task)) {
                    $scope.command["taskId"] = task.id;
                }
                
                $scope.$watch('command', () => {
                    this.commandService.isValid(validatorId, commandId, $scope.command)
                        .then(valid => {
                            if (valid) {
                                element.removeAttr('disabled');
                                element.unbind('click', stopClick)
                            } else {
                                element.attr('disabled', 'disabled');
                                element.bind('click', stopClick);
                            }
                        });
                }, true);
            }
        }
        
        public static factory(): ng.IDirectiveFactory {
            /** @ngInject **/
            let directive = (commandService: CommandService, $stateParams: ng.ui.IStateParamsService,
                    messagesService: app.support.messaging.MessagesService,
                    $rootScope: ng.IRootScopeService) => new CommandDirective(commandService, $stateParams, messagesService, $rootScope);
            return directive;
        }
    }
    
    export interface SaveTaskDirectiveScope extends ng.IScope {
        saveTask: Command;
        preSave: Function;
        validator: string;
    }
    
    /**
     * Bot&atilde;o de um comando espec&iacute;fico
     * Ex. de uso: 
     * &lt;button save-task="vm.command" validator="validadorRegistro"&gt;Salvar&lt;/button&gt; 
     */
    export class SaveTaskDirective implements ng.IDirective {
        
        public restrict: string = 'A';
        public scope: Object = {
            saveTask: '=', //obrigatório, comando que será validado
            preSave: '&', //opcional, callback para ser executado antes do salvamento 
            validator: '@' //opcional, validador para o comando, caso não informado procura por um com mesmo id do comando
        };
        
        public constructor(private commandService: CommandService, private messagesService, private $stateParams: ng.ui.IStateParamsService,
                private $rootScope: ng.IRootScopeService, private $q: ng.IQService) { }
        
        public link: ng.IDirectiveLinkFn = ($scope: SaveTaskDirectiveScope, element: ng.IAugmentedJQuery): void => {
            
            if (angular.isUndefined($scope.saveTask)) {
                throw new Error("Comando não definido!");
            }
            
            let task: ITask = <ITask> this.$stateParams["task"];
            
            if (angular.isObject(task)) {
                $scope.saveTask["taskId"] = task.id;
            }
            
            let commandId: string = task.command;
            let validatorId: string = angular.isString($scope.validator) ? $scope.validator : commandId;
            
            if (angular.isString(commandId) && commandId.length > 0) {
                
                let taskValid: boolean;
                
                let saveTaskClick = () => {
                    if (!this.$stateParams["task"].selfAssigned) {
                        this.$rootScope.$broadcast("tentouExecutarTarefaSemAssumir");
                        this.messagesService.error("É necessário assumir a tarefa antes de salvá-la.");
                    } else {
                        this.$q.when($scope.preSave())
                            .then(() => {
                                 this.commandService.saveTask(task, taskValid, $scope.saveTask)
                                     .then(() => {
                                         this.messagesService.success("Tarefa salva com sucesso!");
                                     }, () => {
                                         this.messagesService.error("Erro ao salvar tarefa!");
                                     });
                            });
                    }
                }
                element.bind('click', saveTaskClick);
                
                $scope.$watch('saveTask', () => {
                    this.commandService.isValid(validatorId, commandId, $scope.saveTask)
                        .then(valid => {
                            taskValid = valid;
                        });
                }, true);
            }
        }
        
        public static factory(): ng.IDirectiveFactory {
            /** @ngInject **/
            let directive = (commandService: CommandService, messagesService, $stateParams: ng.ui.IStateParamsService,
                    $rootScope: ng.IRootScopeService, $q: ng.IQService) => 
                new SaveTaskDirective(commandService, messagesService, $stateParams, $rootScope, $q);
            return directive;
        }
    }
    
    export interface OpenCommandScope extends ng.IScope {
        id: string;
        showNotAllowed: boolean;
    }

    export class OpenCommand implements ng.IDirective {
        public restrict: string = 'A';

        public constructor(private commandService: CommandService) { }

        public link: ng.IDirectiveLinkFn = ($scope: OpenCommandScope, element: ng.IAugmentedJQuery, attrs: ng.IAttributes): void => {
            this.commandService.findByIdAndContext(attrs['id'], attrs['context']).then((commandConfig) => {
                element.show();
            }, () => {
                if (attrs['showNotAllowed']) {
                    element.hide();
                } else {
                    element.attr('disabled', 'disabled');
                }
            });
        }

        public static factory(): ng.IDirectiveFactory {
            /** @ngInject **/
            let directive = (commandService: CommandService) => new OpenCommand(commandService);
            return directive;
        }

    }

    export class ShowIfHasPermission implements ng.IDirective {
        public restrict: string = 'A';

        public constructor(private authService: AuthService, private commandService: CommandService) { }

        public link: ng.IDirectiveLinkFn = ($scope: OpenCommandScope, element: ng.IAugmentedJQuery, attrs: ng.IAttributes): void => {
            let search = <IPermission> {
                context: !!attrs['context'] ? attrs['context'] : null,
                id: attrs['showIfHasPermission'],
                readOnly: !!attrs['readOnly'] ? $scope.$eval(attrs['readOnly']) : false
            };
            
            this.authService.getUser().then((user) => {
                let found = user.componentes.find(p => this.comparePermission(p, search));
                if (found) {
                    element.show();
                } else {
                    element.hide();    
                }
            }, () => {
                this.commandService.findByIdAndContext(search.id, search.context)
                    .then(() => {
                        element.show();
                    }, () => {
                        element.hide();
                    });
            });
        }

        public static factory(): ng.IDirectiveFactory {
            /** @ngInject **/
            let directive = (authService: AuthService, commandService: CommandService) => {
                return new ShowIfHasPermission(authService, commandService);   
            };
            return directive;
        }
        
        private comparePermission(user: IPermission, search: IPermission): boolean {
            return this.compareContext(user.context, search.context) &&
                user.id === search.id && (!user.readOnly || user.readOnly === search.readOnly);
        }
        
        private compareContext(userCtxt: string, searchCtxt: string): boolean {
            if (!!searchCtxt) {
                return userCtxt === searchCtxt;
            } else {
                return true;
            }
        }
    }
    
    angular
        .module('app.support.command')
        .directive('commands', CommandListDirective.factory())
        .directive('command', CommandDirective.factory())
        .directive('saveTask', SaveTaskDirective.factory())
        .directive('openCommand', OpenCommand.factory())
        .directive('showIfHasPermission', ShowIfHasPermission.factory());
    
}